/**
 * `@rista/core/printing` — the POS adapter for `@rista/printing`. See docs/flows/PRINTING.md.
 *
 *   @rista/printing        every printing RULE, pure
 *   @rista/core/printing   this: the redux half of the context, the status sink, the host seam
 *   apps/{web,mobile}      the hardware: a transport, a rasteriser, the device-only reads
 *
 * An app registers its host ONCE, in `bootstrapCoreBridges.ts`. Callers — the kiosk today, the POS
 * behind its rollout gate later — use `printOnClose` / `printOnSave`.
 *
 * ⚠️ Not an `effects-bridge` entry, for the reason `kiosk/payment/transport.ts` gives: those degrade
 * to a silent no-op when unwired. An unregistered printer host is VISIBLE here instead —
 * `getPrinting()` answers null.
 */
import { createPoller, createPrinting, ticketsHandedToPoller } from "@rista/printing";
import type { FetchUnprinted, Poller, PrintContext, PrintReport, PrintTransport, Printing, Rasterizer, Skip } from "@rista/printing";

import { DEFAULT_PRINTER_MSG } from "../data/constants";
import { notifyError, pollForTickets } from "../effects-bridge";
import { getErrorMessage } from "../utils/posErrors";
import { buildPrintContext, type HostSnapshot } from "./context";
import { branchPrintsThroughEngine } from "./rollout";
import { printStatusSink } from "./status";
import { skipNotice } from "./skipNotice";
import { alerts } from "../alert";
import { capabilities } from "../capabilities/registry";
import { copy } from "../copy";
import { toasts } from "../toast";

export type { HostSnapshot } from "./context";
export { buildPrintContext } from "./context";
export { printStatusSink, statusSink } from "./status";
export { PRINTING_ALLOWLIST } from "./rollout";

export interface PrintHost {
  transport: PrintTransport;
  snapshot(): HostSnapshot;
  raster?: Rasterizer;
  /** ms between jobs — 0 where the host already spaces them (Electron's main process) */
  gapMs?: number;
  timeoutMs?: number;
  /**
   * v1 `facade/ServiceFacade.getUnprintedTicketsFn` — the poller's one fetch. A host that supplies
   * it gets the package's poller; one that does not keeps v1's (the effects bridge).
   */
  fetchUnprinted?: FetchUnprinted;
  /**
   * What the operator sees when something does not print — v1's toast or warning modal. The app
   * owns it because the message is UI: web raises a modal for a missing default printer and a toast
   * for a ticket with no printer; mobile raises the same two through its own components. Called as
   * each skip happens, not after the prints finish, which is v1's order.
   */
  notifySkip?(skip: Skip): void;
}

let printing: Printing | null = null;
let poller: Poller | null = null;
let notifySkip: ((skip: Skip) => void) | undefined;
/** the registered host's snapshot, for the one question answered outside a print request */
let contextOf: (() => PrintContext) | null = null;

export function registerPrintHost(host: PrintHost): void {
  poller?.stop();
  poller = null;
  notifySkip = host.notifySkip;
  const context = () => buildPrintContext(host.snapshot());
  contextOf = context;
  printing = createPrinting({
    transport: host.transport,
    raster: host.raster,
    status: printStatusSink,
    context,
    gapMs: host.gapMs,
    timeoutMs: host.timeoutMs,
  });
  if (host.fetchUnprinted) {
    poller = createPoller({
      printing,
      context,
      fetchUnprinted: host.fetchUnprinted,
      onEvent: (event) => {
        // `unprintable` is the one an operator needs: this device cannot print that sale, so the
        // server lists it again every cycle and nothing ever comes out. v1's label path toasted it;
        // until S4 wires `notifySkip` (which must not reach the kiosk) this log is the only signal.
        if (event.type === "error" || event.type === "stopped" || event.type === "unprintable") {
          console.log("[printing] poller", event);
        }
      },
    });
  }
}

export function getPrinting(): Printing | null {
  return printing;
}

/**
 * 🔴 THE ROLLOUT GATE — the one line at the top of each v1 print entry:
 *
 *     if (printingEnabledFor()) return printBill(sale, printer, { … });
 *
 * True only when this app registered a printer host AND this branch is on `PRINTING_ALLOWLIST`
 * (which ships empty). Everything else keeps printing through v1, unchanged.
 */
export function printingEnabledFor(businessData?: any): boolean {
  return !!printing && branchPrintsThroughEngine(businessData);
}

/**
 * Starts the PACKAGE's print-device loop — v1 `utils/polling.pollForTickets()` — and answers whether
 * it did. It only does where this branch prints through the engine (`printingEnabledFor`) and the
 * host wired a fetch; everywhere else the device's poller is still v1's, and the caller starts that
 * one instead. Each app's effects-bridge `pollForTickets` is exactly that caller:
 *
 *     pollForTickets: () => { if (!startPolling()) pollForTickets(); }
 *
 * 🔴 Never both. Two loops on one print device would each claim and print the same tickets. The
 * loop asks the settings whether this device polls at all, so a start where it does not is free.
 *
 * A start of a RUNNING loop is v1's kick, not a second loop: it asks for the next poll 200 ms from
 * now, as `pollForTickets()` did (`Poller.start`). That is what web's push-notification handlers,
 * its 10 s timer and the polling hand-off rely on to print a new order at once. ⚠️ Those web
 * callers reach v1's `pollForTickets` DIRECTLY (`commonComponents/Layout/homeLayout.tsx`), not
 * through the bridge — so "one loop" only holds once S4's gate is inside v1 `pollForTickets` itself.
 */
export function startPolling(): boolean {
  if (!poller || !printingEnabledFor()) return false;
  poller.start();
  return true;
}

/** Stops the package's poller. v1's has no stop — it stops itself when its settings change. */
export function stopPolling(): void {
  poller?.stop();
}

/**
 * Runs one print request. Never rejects: printing is a side effect of an order that has already
 * been written, and a failure here must not surface as a failed order.
 *
 * A polling channel's KOTs are handed to this device's poller, when this device is the print
 * device — through the effects bridge, which starts whichever loop the device runs (see
 * `startPolling`), exactly as v1 `printSaleTickets` called `pollForTickets()`.
 */
type OnSkip = (skip: Skip) => void;

/**
 * What the operator sees for a skip, as it happens (v1 toasts before the prints end): the host's
 * own `notifySkip` if it has one, else v2's alert or toast (`skipNotice`). 🔴 Silent in kiosk
 * mode, where a customer stands at the screen (PRINTING_HANDOFF.md §7 step 3; decided 2026-10-05).
 */
export function reportSkip(skip: Skip): void {
  if (capabilities().facts().env.kiosk) return;
  if (notifySkip) return notifySkip(skip);
  const notice = skipNotice(skip);
  if (!notice) return;
  if (notice.as === "alert") alerts.show(copy(notice.message));
  else toasts.show("error", copy(notice.message));
}

/**
 * Every request reports its skips as they happen. A caller's own `onSkip` wins over `reportSkip`:
 * that is how a My Orders v2 screen gets v1's messages (`toastSkip`).
 */
const options = (onSkip?: OnSkip) => ({ onSkip: onSkip ?? reportSkip });

/**
 * v1 printHelper's POS messages for the skips it announced, as toasts: `printSale`'s "A default
 * printer must be selected." (a warning modal on web v1, an alert on mobile v1) and
 * `printSaleTickets` / `printItemLabelTemplate`'s "configure a printer". The rest were silent in
 * v1. 🔴 A POS caller passes it; never the kiosk, where a customer would read it.
 */
export function toastSkip(skip: Skip): void {
  if (skip.reason === "noDefaultPrinter") notifyError(DEFAULT_PRINTER_MSG);
  else if (skip.reason === "noPrinterForTicket" || skip.reason === "noLabelPrinter") notifyError(getErrorMessage("UI_ERR_SELECT_PRINTER_FROM_POS"));
}

/** the engine's own failure codes, as the shell's `ERR_*` messages that v1 already shows */
const FAILURE_MESSAGE: Record<string, string> = { TIMEOUT: "ERR_PRINT_TIMEOUT", TRANSPORT_ERROR: "ERR_IO" };

/**
 * One toast per distinct reason for the jobs that did not print — a dead printer fails every KOT
 * of a save, and the operator needs the reason once. A code with no message (`UNSUPPORTED_*`: a
 * plain browser tab has no printer at all, where v1 was silent too) stays silent.
 */
function toastFailures(report: PrintReport): void {
  const seen = new Set<string>();
  for (const { outcome } of report.jobs) {
    if (outcome.ok) continue;
    const key = FAILURE_MESSAGE[outcome.code] ?? outcome.code;
    const message = getErrorMessage(key);
    if (message === key || seen.has(message)) continue;
    seen.add(message);
    notifyError(message);
  }
}

async function run(request: (p: Printing) => Promise<PrintReport>, onSkip?: OnSkip): Promise<PrintReport | null> {
  if (!printing) return null;
  try {
    const report = await request(printing);
    if (report.skipped.some((skip) => skip.reason === "polledByThisDevice")) pollForTickets();
    // a POS caller that asked for v1's no-printer toasts gets the failed-send ones too (never the kiosk)
    if (onSkip === toastSkip) toastFailures(report);
    return report;
  } catch (error) {
    console.log("[printing] request failed", error);
    return null;
  }
}

/** A closed sale: the bill when this device prints one, then its pending KOTs — v1 `handlePrintLogic`. */
export function printOnClose(sale: any): Promise<PrintReport | null> {
  return run((p) => p.saleClosed(sale, options()));
}

/** A saved sale: its pending KOTs only — v1 `printSaleTickets(res, res.tickets, 1)`. */
export function printOnSave(sale: any): Promise<PrintReport | null> {
  return run((p) => p.print("tickets", { sale, tickets: sale?.tickets || [] }, options()));
}

/**
 * The rest of the documents, one entry point each. Each mirrors the v1 export S4 will put its
 * rollout gate on — `if (printingEnabledFor()) return <entry>(…)` — so each returns something that
 * gate can return in v1's place: a report, where no caller reads v1's return value, and v1's own
 * synchronous answer for `printTickets`, where one does. None of them toasts — what the operator
 * sees comes through `PrintHost.notifySkip`, while the prints are still going out.
 */

/** v1 `printItemLabelTemplate(sale, itemLabelSettings)` — item label stickers. */
export function printItemLabels(sale: any, itemLabelSettings?: any, onSkip?: OnSkip): Promise<PrintReport | null> {
  return run((p) => p.print("label", { sale, itemLabelSettings }, options(onSkip)), onSkip);
}

/** v1 `printSalesummary(summary, printer)` — the cashier's summary report. */
export function printSalesSummary(summary: any, printer?: any): Promise<PrintReport | null> {
  return run((p) => p.print("salesSummary", { summary, printer }, options()));
}

/** v1 `printbill(printer)` plus the shell call its callers made — printer setup's test print. */
export function printTestPage(printer: any): Promise<PrintReport | null> {
  return run((p) => p.print("testPrint", { printer }, options()));
}

/** v1 `handlePrintData(content, printer)` — content the merchant panel composed. */
export function printRawContent(content: string, printer?: any): Promise<PrintReport | null> {
  return run((p) => p.print("raw", { content, printer }, options()));
}

/**
 * v1 `printSale(saleData, printer, copies, type, isAutoPrintBills, isManual)` — one bill, or one
 * prepared ticket, which v1 prints through the same function. The v1 signature is translated at
 * the call site so this stays the flow's vocabulary rather than v1's.
 */
export function printBill(
  sale: any,
  printer?: any,
  input: { copies?: number; giftReceipt?: boolean; copiesFromChannel?: boolean; manual?: boolean } = {},
  onSkip?: OnSkip,
): Promise<PrintReport | null> {
  return run((p) => p.print("bill", { sale, printer, ...input }, options(onSkip)), onSkip);
}

/**
 * v1 `printSaleTickets(sale, tickets, retryCount, isManualPrint)` — a sale's kitchen tickets.
 *
 * 🔴 It returns what v1 returns, SYNCHRONOUSLY: `true` when these KOTs are the print device's
 * poller's to print — whichever device that is — and `false` otherwise. The print still runs, in
 * the background, and never rejects. Web `MyOrderCard` reads that value: an accepted order's closed
 * bill prints on this terminal only when it is false, because the print device prints it otherwise.
 * A promise here would be truthy for every order and stop that bill printing everywhere.
 */
export function printTickets(sale: any, tickets?: any[], manual = false, onSkip?: OnSkip): boolean {
  const polled = ticketsGoToPoller(sale, manual);
  void run((p) => p.print("tickets", { sale, tickets: tickets ?? sale?.tickets ?? [], manual }, options(onSkip)), onSkip);
  return polled;
}

/**
 * `ticketsHandedToPoller` on this terminal's snapshot. False with no host registered — v1 answers
 * then — and when the snapshot cannot be read: this terminal then prints the bill a caller would
 * have left to the print device, and a duplicate bill is a lesser failure than a missing one.
 */
function ticketsGoToPoller(sale: any, manual: boolean): boolean {
  if (!contextOf) return false;
  try {
    return ticketsHandedToPoller({ sale, manual }, contextOf());
  } catch (error) {
    console.log("[printing] could not read the snapshot for a ticket hand-off", error);
    return false;
  }
}

/** v1 `openCashDrawer(printer)` — the header's drawer button, with no bill attached. */
export function printCashDrawer(printer?: any): Promise<PrintReport | null> {
  return run((p) => p.print("cashDrawer", { printer }, options()));
}
